/**
 * The town somebody else built.
 *
 * *"in the siege, there was nothing in the enemy town but few walls no
 * buildings"* — and that was exactly true. A fortified province handed to
 * battle mode got `buildFieldWorks`: forty-five tiles of hedge with two gaps in
 * it, no roofs, no streets, nothing to take. Taking a capital was walking at a
 * fence.
 *
 * This raises a real one. Three rules decide everything below.
 *
 * **1. It is built out of the player's own catalogue.** Every roof here is a
 * `BUILDING_TYPES` entry placed through `placeBuilding`, exactly as the player
 * places theirs; every wall goes down through `setWall`. There is deliberately
 * no second vocabulary of "enemy buildings" — one would drift from the real one
 * within a release, and the renderer, the targeting pass, the flow field and
 * the rubble layer would all have to learn about it. The AI builds a Barracks
 * because a Barracks is what a town of this people at this Age has.
 *
 * **2. What is in it is decided by the holding, not by the scenario.** A
 * Hamlet at tier 1 gets a palisade ring, a hall and a dozen roofs. A Seat of the
 * Realm at tier 10 gets a bastion circuit with towers on it, a market, a drill
 * yard, a smeltery and sixty buildings. The Age is read off the rung — a Great
 * City builds like a fifth-Age town because that is what a Great City is — so
 * the strategic layer's one number for "how much has been spent on this place"
 * decides what you have to fight through, without needing a second one.
 *
 * **3. It is laid out along streets, because streets are what the rest of the
 * simulation already understands.** Roads go down first, from every gate to the
 * middle, plus a ring road inside the wall. Plots are then refused any tile a
 * road is on and *preferred* where they touch one — which is `touchesRoad` in
 * `districts.ts`, the same rule that pays the player's own producers. The
 * result is a town with a shape rather than a solid block of sheds, and it is a
 * shape the pathfinder can get through: a besieger's route down a street is
 * cheap, and it is the streets that make the difference between a company
 * getting into the middle of a town and one grinding through its outskirts.
 *
 * Nothing here reads a clock or `Math.random`; every roll comes off the `Rng`
 * the caller hands in, so the same battle seed builds the same town.
 */

import { BUILDING_TYPES, buildingTypeIndex, GATE_DEF, TOWER_DEF, wallTierDef } from '../../data/buildings.ts';
import { Terrain, WallKind } from '../types.ts';
import { placeBuilding, setWall, type World } from '../world.ts';
import { nextFloat, nextInt, type Rng } from '../../core/rng.ts';
import { clamp } from '../../core/math.ts';

/** What the strategic layer knows about a place, and all this needs from it. */
export interface TownPlan {
  cx: number;
  cy: number;
  /** Whose town it is. Its walls should be `World.wallTeam`. */
  team: number;
  /** The holding's rung, 1–10. See `TIER_NAMES` in `campaign/holdings.ts`. */
  tier: number;
  /** The province's fortification level, 0–3. Decides the wall and the towers. */
  fort: number;
  /** A capital is a rung and a half grander than its tier alone says. */
  capital: boolean;
  /** Which people built it: gates race-locked buildings. `World.raceId`. */
  raceId: string;
}

/** What was actually raised. Returned so a harness can assert on it. */
export interface TownReport {
  buildings: number;
  wallTiles: number;
  gates: number;
  towers: number;
  radius: number;
  age: number;
}

/**
 * The Age a holding builds at.
 *
 * Two rungs to an Age, and a capital counts as one rung grander. Deliberately
 * derived rather than stored: the campaign has no Age of its own, a nation's
 * technology is not modelled province by province, and inventing a second
 * number for "how advanced is this place" would immediately disagree with the
 * first one. What a player reads off it is legible — a Market Town is
 * second-Age, a Great City is fifth — which is the property that matters.
 */
export function townAge(tier: number, capital: boolean): number {
  return clamp(1 + Math.floor((tier - 1 + (capital ? 1 : 0)) / 2), 1, 5);
}

/** How far the wall stands from the middle, in tiles. */
export function townRadius(tier: number, capital: boolean): number {
  return Math.round(clamp(9 + tier * 1.35 + (capital ? 2 : 0), 10, 26));
}

/**
 * The programme, in the order it is laid out from the middle outward.
 *
 * A list of ids and how many of each, because that is the whole of what "a
 * town of this people at this Age looks like" is. Order is placement order and
 * placement order is geography: the seat and the things that answer to it in
 * the middle, the trades and the stores round them, dwellings filling out to
 * the wall, and the fighting platforms last so they end up on the rampart.
 *
 * Ids that this people may not raise, or that the Age has not reached, fall out
 * in `pickProgramme` — so a Hamlet's list is a handful of huts and a hall and
 * the same code produced it.
 */
interface Want {
  id: string;
  /** Base count at tier 1, and how much each further rung adds. */
  base: number;
  per: number;
}

const SEATS = ['hollow_crown', 'gilded_hall', 'war_room', 'academy', 'tavern'];

const PROGRAMME: readonly Want[] = [
  // The middle: what a town is administered from.
  { id: 'chapel', base: 1, per: 0.1 },
  { id: 'tavern', base: 1, per: 0.1 },
  { id: 'market', base: 0, per: 0.14 },
  { id: 'war_room', base: 0, per: 0.12 },
  { id: 'academy', base: 0, per: 0.12 },
  // The garrison quarter.
  { id: 'barracks', base: 1, per: 0.2 },
  { id: 'archery_range', base: 0, per: 0.16 },
  { id: 'armoury', base: 0, per: 0.14 },
  { id: 'stable', base: 0, per: 0.12 },
  { id: 'drill_yard', base: 0, per: 0.1 },
  { id: 'siege_yard', base: 0, per: 0.08 },
  { id: 'infirmary', base: 0, per: 0.1 },
  // The trades. Nothing that has to stand on a particular kind of ground —
  // those are the hinterland's, below, and a quarry inside a town is a lie.
  { id: 'storehouse', base: 1, per: 0.16 },
  { id: 'granary', base: 1, per: 0.14 },
  { id: 'bakery', base: 1, per: 0.14 },
  { id: 'sawmill', base: 0, per: 0.16 },
  { id: 'mill', base: 0, per: 0.12 },
  { id: 'workshop', base: 0, per: 0.12 },
  { id: 'smelter', base: 0, per: 0.12 },
  { id: 'counting_house', base: 0, per: 0.1 },
  { id: 'smeltery', base: 0, per: 0.08 },
  { id: 'great_forge', base: 0, per: 0.06 },
];

/**
 * Where the people live: the bulk of any town, and the reason a taken capital
 * reads as a place rather than as a compound.
 *
 * A list of its own, and laid **last of everything inside the wall**, because
 * dwellings are what a town has as much of as it has room for. Left in the
 * programme above they simply ate every remaining plot, and a town with thirty
 * houses and no Grudge Hall in it is a town with no people in it.
 */
const DWELLINGS: readonly Want[] = [
  { id: 'house', base: 5, per: 2.1 },
  { id: 'longhouse', base: 0, per: 0.5 },
];

/** Raised on the rampart, last, so they end up where a tower belongs. */
const RAMPART: readonly Want[] = [
  { id: 'arrow_tower', base: 1, per: 0.22 },
  { id: 'ballista_tower', base: 0, per: 0.16 },
  { id: 'watchtower', base: 1, per: 0.18 },
];

/** Outside the wall: the fields the town eats out of. */
const HINTERLAND: readonly Want[] = [{ id: 'farm', base: 2, per: 0.4 }];

/** Can this people, at this Age, raise this at all? */
function allowed(idx: number, age: number, raceId: string): boolean {
  const def = BUILDING_TYPES[idx];
  if (def.category === 'core') return false;
  if (def.tier > age) return false;
  if (def.race !== '' && def.race !== raceId) return false;
  return true;
}

/** The programme, filtered to what this town could actually have built. */
function pickProgramme(
  wants: readonly Want[],
  tier: number,
  age: number,
  raceId: string,
): Array<{ idx: number; n: number }> {
  const out: Array<{ idx: number; n: number }> = [];
  for (const want of wants) {
    const idx = buildingTypeIndex(want.id);
    if (idx < 0 || !allowed(idx, age, raceId)) continue;
    const n = Math.floor(want.base + want.per * (tier - 1) * 2);
    if (n <= 0) continue;
    out.push({ idx, n });
  }
  return out;
}

/** Is every tile of this footprint free for a roof? */
function plotFree(w: World, tx: number, ty: number, bw: number, bh: number, roads: Uint8Array): boolean {
  const map = w.map;
  for (let y = ty - 1; y <= ty + bh; y++) {
    for (let x = tx - 1; x <= tx + bw; x++) {
      if (!map.inBounds(x, y)) return false;
      const k = map.idx(x, y);
      // A one-tile skirt of clearance, so the town has gaps a man can walk
      // down and does not fuse into one solid block — which is the same fault
      // as a wall with no gate, one layer in.
      if (map.building[k] >= 0) return false;
      const inside = x >= tx && x < tx + bw && y >= ty && y < ty + bh;
      if (!inside) continue;
      if (map.wall[k] !== WallKind.None) return false;
      if (roads[k] !== 0) return false;
      const t = map.terrain[k];
      if (t === Terrain.River || t === Terrain.Deep || t === Terrain.Marsh) return false;
    }
  }
  return true;
}

/** Does this footprint have a street on it? */
function onAStreet(w: World, tx: number, ty: number, bw: number, bh: number, roads: Uint8Array): boolean {
  const map = w.map;
  for (let y = ty - 1; y <= ty + bh; y++) {
    for (let x = tx - 1; x <= tx + bw; x++) {
      if (!map.inBounds(x, y)) continue;
      if (roads[map.idx(x, y)] !== 0) return true;
    }
  }
  return false;
}

/**
 * Raise a town, walls and all. Returns what was actually built.
 *
 * Every roll comes off `rng`, and the order of the rolls is fixed by the order
 * of the loops, so the same seed and the same plan give the same town on every
 * machine and in every replay.
 */
export function raiseTown(w: World, rng: Rng, plan: TownPlan): TownReport {
  const map = w.map;
  const age = townAge(plan.tier, plan.capital);
  const R = townRadius(plan.tier, plan.capital);
  const cx = Math.round(plan.cx);
  const cy = Math.round(plan.cy);

  const report: TownReport = { buildings: 0, wallTiles: 0, gates: 0, towers: 0, radius: R, age };

  /**
   * The circuit.
   *
   * Four gates on the cardinals rather than one, and that is a *design*
   * decision rather than generosity. A single door turns an assault into a
   * queue: every company in the army routes to the same tile, the ones behind
   * stand still, and the fight is decided by who is at the front. Four gives
   * an attacker a choice of approach and a defender something to spread over,
   * which is what makes a wall a decision instead of a bottleneck.
   */
  const tierDef = clamp(1 + plan.fort, 1, 3);
  const wallHp = wallTierDef(tierDef).hp;
  const gateHalf = 1;
  const gateAngles = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
  const isGateTile = (x: number, y: number): boolean => {
    for (const a of gateAngles) {
      const gx = Math.round(cx + Math.cos(a) * R);
      const gy = Math.round(cy + Math.sin(a) * R);
      if (Math.abs(x - gx) <= gateHalf && Math.abs(y - gy) <= gateHalf) return true;
    }
    return false;
  };

  for (let y = cy - R - 1; y <= cy + R + 1; y++) {
    for (let x = cx - R - 1; x <= cx + R + 1; x++) {
      if (!map.inBounds(x, y)) continue;
      const d = Math.hypot(x - cx, y - cy);
      if (d < R - 0.55 || d > R + 0.55) continue;
      if (isGateTile(x, y)) {
        setWall(w, x, y, WallKind.Gate, GATE_DEF.hp, tierDef);
        report.gates++;
      } else {
        setWall(w, x, y, WallKind.Wall, wallHp, tierDef);
        report.wallTiles++;
      }
    }
  }

  /**
   * Towers on the circuit, on the diagonals, at a fort level that has earned
   * them. Two tiles square and laid on the wall itself — see `TOWER_DEF` and
   * `curtain.ts`; a tower is a wall as far as pathing, solidity and the flow
   * field are concerned, and it is the only part of the curtain a company can
   * be posted on.
   */
  if (plan.fort >= 2) {
    for (const a of [Math.PI / 4, (3 * Math.PI) / 4, (-3 * Math.PI) / 4, -Math.PI / 4]) {
      const tx = Math.round(cx + Math.cos(a) * R);
      const ty = Math.round(cy + Math.sin(a) * R);
      let laid = 0;
      for (let dy = 0; dy < 2; dy++) {
        for (let dx = 0; dx < 2; dx++) {
          if (!map.inBounds(tx + dx, ty + dy)) continue;
          setWall(w, tx + dx, ty + dy, WallKind.Tower, TOWER_DEF.hp, tierDef);
          laid++;
        }
      }
      if (laid > 0) report.towers++;
    }
  }

  /**
   * The streets, laid before anything is built on them.
   *
   * A spoke from each gate to the middle and one ring road inside the wall.
   * Kept as a side table rather than read back off `map.terrain`, because the
   * ground under a town is not all `AshPlain` and a "is this a road" test that
   * reads terrain would also have to know what a road is *not*.
   */
  const roads = new Uint8Array(map.terrain.length);
  const layRoad = (x: number, y: number): void => {
    if (!map.inBounds(x, y)) return;
    const k = map.idx(x, y);
    if (map.wall[k] !== WallKind.None) return;
    if (map.terrain[k] === Terrain.Deep || map.terrain[k] === Terrain.River) return;
    map.terrain[k] = Terrain.Road;
    roads[k] = 1;
  };
  for (const a of gateAngles) {
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    for (let r = 0; r <= R; r += 0.5) {
      layRoad(Math.round(cx + dx * r), Math.round(cy + dy * r));
    }
  }
  const ringR = Math.max(4, R - 3);
  const ringSteps = Math.max(24, Math.round(ringR * 8));
  for (let s = 0; s < ringSteps; s++) {
    const a = (s / ringSteps) * Math.PI * 2;
    layRoad(Math.round(cx + Math.cos(a) * ringR), Math.round(cy + Math.sin(a) * ringR));
  }
  map.terrainVersion++;

  /**
   * The plots, from the middle outward.
   *
   * Concentric rings of candidate corners with a per-ring phase off the `Rng`,
   * so a town is not four identical quadrants and two towns of the same tier
   * are not the same town. Sorted by radius, which is what makes the
   * programme's order into the town's geography.
   */
  const plots: Array<{ x: number; y: number; r: number }> = [];
  for (let r = 3; r <= R - 1.5; r += 2.6) {
    const n = Math.max(5, Math.round((Math.PI * 2 * r) / 4.2));
    const phase = nextFloat(rng) * Math.PI * 2;
    for (let j = 0; j < n; j++) {
      const a = phase + (j / n) * Math.PI * 2;
      plots.push({ x: Math.round(cx + Math.cos(a) * r), y: Math.round(cy + Math.sin(a) * r), r });
    }
  }

  const place = (idx: number, tx: number, ty: number): boolean => {
    const def = BUILDING_TYPES[idx];
    if (!plotFree(w, tx, ty, def.w, def.h, roads)) return false;
    if (placeBuilding(w, idx, tx, ty, plan.team, true) < 0) return false;
    report.buildings++;
    return true;
  };

  // The seat: the grandest hall this people has reached, dead centre.
  for (const id of SEATS) {
    const idx = buildingTypeIndex(id);
    if (idx < 0 || !allowed(idx, age, plan.raceId)) continue;
    const def = BUILDING_TYPES[idx];
    if (place(idx, cx - (def.w >> 1), cy - (def.h >> 1))) break;
  }

  /**
   * The programme against the plots, in two passes over each want.
   *
   * The first pass will only take a plot that touches a street, which is what
   * puts the town on its roads rather than merely near them; the second takes
   * whatever is left, so a want is never silently dropped because the streets
   * were full. Two passes rather than a score, because a score here would be
   * `districts.ts` written a second time in a file that does not need it.
   */
  let cursor = 0;
  const runProgramme = (wants: Array<{ idx: number; n: number }>): void => {
    for (const want of wants) {
      const def = BUILDING_TYPES[want.idx];
      for (let made = 0; made < want.n; ) {
        let placed = false;
        for (let pass = 0; pass < 2 && !placed; pass++) {
          for (let scan = 0; scan < plots.length; scan++) {
            const p = plots[(cursor + scan) % plots.length];
            const tx = p.x - (def.w >> 1);
            const ty = p.y - (def.h >> 1);
            if (!plotFree(w, tx, ty, def.w, def.h, roads)) continue;
            if (pass === 0 && !onAStreet(w, tx, ty, def.w, def.h, roads)) continue;
            if (!place(want.idx, tx, ty)) continue;
            cursor = (cursor + scan + 1) % plots.length;
            placed = true;
            break;
          }
        }
        if (!placed) break;
        made++;
      }
    }
  };

  runProgramme(pickProgramme(PROGRAMME, plan.tier, age, plan.raceId));

  /**
   * The buildings only this people has, found in the catalogue rather than
   * listed here.
   *
   * `BuildingType.race` already says "only the Karrun raise a Grudge Hall", and
   * that field is the answer to *"is this two peoples or one town in two
   * tints"* — the question `src/tools/townsheet.ts` exists to ask of the art.
   * Reading it means a people added to `data/races.ts` tomorrow builds its own
   * town the day it lands, with nothing written here; a table of ids per race
   * would have to be remembered, and would not be.
   *
   * One of each, and a second of each past the seventh rung: these are the
   * signature buildings, and a town with four Ossuaries in it is not a town
   * with a character, it is a town with a bug.
   */
  const signature: Array<{ idx: number; n: number }> = [];
  for (let i = 0; i < BUILDING_TYPES.length; i++) {
    const def = BUILDING_TYPES[i];
    if (def.race !== plan.raceId) continue;
    if (!allowed(i, age, plan.raceId)) continue;
    signature.push({ idx: i, n: plan.tier >= 7 ? 2 : 1 });
  }
  runProgramme(signature);
  runProgramme(pickProgramme(DWELLINGS, plan.tier, age, plan.raceId));

  // The rampart last, on the innermost plots that still touch the wall.
  const rampart = plots.filter((p) => p.r >= R - 6);
  for (const want of pickProgramme(RAMPART, plan.tier, age, plan.raceId)) {
    const def = BUILDING_TYPES[want.idx];
    let made = 0;
    for (let scan = 0; scan < rampart.length && made < want.n; scan++) {
      const p = rampart[(scan * 7 + nextInt(rng, 0, 3)) % rampart.length];
      if (place(want.idx, p.x - (def.w >> 1), p.y - (def.h >> 1))) made++;
    }
  }

  // The hinterland, outside the wall, where a besieger walks through it first.
  for (const want of pickProgramme(HINTERLAND, plan.tier, age, plan.raceId)) {
    const def = BUILDING_TYPES[want.idx];
    let made = 0;
    const n = Math.max(8, want.n * 3);
    for (let j = 0; j < n && made < want.n; j++) {
      const a = nextFloat(rng) * Math.PI * 2;
      const rr = R + 3 + nextFloat(rng) * 6;
      const tx = Math.round(cx + Math.cos(a) * rr) - (def.w >> 1);
      const ty = Math.round(cy + Math.sin(a) * rr) - (def.h >> 1);
      if (place(want.idx, tx, ty)) made++;
    }
  }

  w.dirty.flow = true;
  w.dirty.districts = true;
  return report;
}
